import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { COLORS, FONT_SIZE } from '../../Variables';

const FeedbackAnswersContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 10px;
`;
const FeedbackAnswers = styled.div`
  display: flex;
  flex-direction: column;
  p {
    font-size: ${FONT_SIZE.small};
    font-weight: 700;
    color: ${COLORS.darkGrey};
    line-height: 1.5;
  }
  div {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin: 5px 0 15px 0;
    span {
      display: flex;
      gap: 5px;
      label {
        font-size: ${FONT_SIZE.small};
      }
    }
  }
`;

// Define a functional component FeedbackAnswersFunc that takes feedback_questions and setFeedbackAnswers as props
const FeedbackAnswersFunc = ({ feedback_questions, setFeedbackAnswers }) => {
  // Use React's useState hook to create a state variable selectedAnswers and a function to update it, setSelectedAnswers
  const [selectedAnswers, setSelectedAnswers] = useState({});

  // Define a function handleChange that takes questionId, answerId, and multipleChoice as parameters
  const handleChange = (questionId, answerId, multipleChoice) => {
    // Check if the question allows multiple answers
    if (multipleChoice) {
      // If it does, update the selectedAnswers state
      setSelectedAnswers((prev) => {
        // Get the previous answers for the question or an empty array if there are none
        const previousAnswers = prev[questionId] || [];
        // Check if the answer is already in the selected answers
        if (previousAnswers.includes(answerId)) {
          // If it is, remove it from the selected answers
          return {
            ...prev,
            [questionId]: previousAnswers.filter((id) => id !== answerId),
          };
        } else {
          // If it's not, add it to the selected answers
          return {
            ...prev,
            [questionId]: [...previousAnswers, answerId],
          };
        }
      });
    } else {
      // If the question doesn't allow multiple answers, update the selected answer for the question
      setSelectedAnswers((prev) => ({ ...prev, [questionId]: [answerId] }));
    }
  };

  // Use React's useEffect hook to call setFeedbackAnswers whenever selectedAnswers changes
  useEffect(() => {
    setFeedbackAnswers(selectedAnswers);
  }, [selectedAnswers, setFeedbackAnswers]);

  return (
    <>
      {/* Use the FeedbackAnswersContainer component with a class name of 'answers_container' */}
      <FeedbackAnswersContainer className="answers_container">
        {/* Use the FeedbackAnswers component with a class name of 'answer_container' */}
        <FeedbackAnswers className="answer_container">
          {/* Map over the feedback_questions array and render a div for each question */}
          {feedback_questions.map((question, index) => {
            // Destructure content and answers from the question object
            const { content, answers } = question;
            return (
              // Render a div with a key of the index
              <div key={index}>
                {/* Render a paragraph with the question content */}
                <p>{content}</p>
                {/* Render a div with a class name of 'answers' */}
                <div className="answers">
                  {/* Map over the answers array and render a span for each answer */}
                  {answers.map((answer, index) => (
                    // Render a span with a key of the index
                    <span key={index}>
                      {/* Render an input of type checkbox if the question allows multiple answers, or radio if it doesn't */}
                      {/* The name is 'question-' followed by the question id, and the value is the answer id */}
                      {/* When the input changes, call handleChange with the question id, answer id, and whether the question allows multiple answers */}
                      <input
                        type={question?.multiple_choice ? 'checkbox' : 'radio'}
                        name={`question-${question?.id}`}
                        value={answer?.id}
                        onChange={() =>
                          handleChange(
                            question.id,
                            answer.id,
                            question.multiple_choice
                          )
                        }
                      />
                      {/* Render a label for the input with the answer content */}
                      <label htmlFor={answer?.id}>{answer?.answer}</label>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </FeedbackAnswers>
      </FeedbackAnswersContainer>
    </>
  );
};

// Export the FeedbackAnswersFunc component as the default export
export default FeedbackAnswersFunc;
